// [phase11-v1] Signal status banner — trading-ui.
import { AlertTriangle, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import type { SignalProviderHealth } from "../../lib/signalClient";
import { safeFormatTimestamp } from '../../lib/accountFormatters';

interface Props {
  data: SignalProviderHealth | null;
  loading: boolean;
  error: string | null;
}

function badgeClasses(tone: "green" | "amber" | "red" | "neutral"): string {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border";
  switch (tone) {
    case "green":
      return `${base} bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30`;
    case "amber":
      return `${base} bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30`;
    case "red":
      return `${base} bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30`;
    default:
      return `${base} bg-[var(--card-bg)] text-[var(--muted-text)] border-[var(--card-border)]`;
  }
}

export default function SignalStatusBanner({ data, loading, error }: Props) {
  if (loading && !data) {
    return (
      <div
        data-testid="signal-status-banner"
        data-state="loading"
        className="flex items-center gap-3 rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <Loader2 className="h-5 w-5 animate-spin text-[var(--muted-text)]" />
        <div className="text-xs text-[var(--muted-text)]">
          Loading Signal provider-health…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        data-testid="signal-status-banner"
        data-state="error"
        className="flex items-center gap-3 rounded-2xl border border-red-500/30 bg-red-500/5 p-4"
      >
        <ShieldAlert className="h-5 w-5 text-red-500" />
        <div>
          <div className="text-sm font-bold text-red-700 dark:text-red-300">
            Signal backend unreachable
          </div>
          <div className="text-xs text-[var(--muted-text)] mt-0.5">{error || "no data"}</div>
        </div>
      </div>
    );
  }

  const { status, realtime_data_verified: rdv, blockers, display_message, generated_at_utc } = data;
  // Defensive: backends in flight may omit `providers`; never call Object.values on null.
  const providers = data.providers || {};
  const liveCount = Object.values(providers).filter(
    (p) => p && (p.status === "LIVE" || p.status === "LIVE_PROXY"),
  ).length;
  const total = Object.keys(providers).length;

  const palette =
    status === "LIVE"
      ? {
          border: "border-emerald-500/30",
          bg: "bg-emerald-500/5",
          icon: <CheckCircle2 className="h-5 w-5 text-emerald-500" />,
          title: "SYSTEM STATUS: OPERATIONAL REGIME ACTIVE",
          tone: "green" as const,
        }
      : status === "LIMITED"
      ? {
          border: "border-amber-500/30",
          bg: "bg-amber-500/5",
          icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
          title: "SYSTEM STATUS: LIMITED DATA — DEGRADED PROVIDERS PRESENT",
          tone: "amber" as const,
        }
      : {
          border: "border-red-500/30",
          bg: "bg-red-500/5",
          icon: <ShieldAlert className="h-5 w-5 text-red-500" />,
          title: "SYSTEM STATUS: BLOCKED — CRITICAL PROVIDER FAILURE",
          tone: "red" as const,
        };

  return (
    <div
      data-testid="signal-status-banner"
      data-state={status}
      data-rdv={String(rdv)}
      className={`rounded-2xl border ${palette.border} ${palette.bg} p-4`}
    >
      <div className="flex items-start gap-3">
        {palette.icon}
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
              {palette.title}
            </div>
            <span className={badgeClasses(palette.tone)}>{status}</span>
            <span className={badgeClasses(rdv ? "green" : "red")}>
              realtime_data_verified = {String(rdv)}
            </span>
            <span className={badgeClasses("neutral")}>
              {liveCount}/{total} providers LIVE
            </span>
          </div>
          <div className="text-xs text-[var(--muted-text)] mt-1.5">{display_message}</div>
          {blockers && blockers.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="signal-blockers">
              {blockers.map((b) => (
                <span
                  key={b.provider}
                  className="text-[10px] font-mono bg-red-500/10 text-red-700 dark:text-red-300 border border-red-500/30 px-2 py-0.5 rounded"
                  title={b.reason || ""}
                >
                  {b.provider}:{b.status}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="text-[10px] text-[var(--muted-text)] font-mono whitespace-nowrap">
          {safeFormatTimestamp(generated_at_utc, { mode: 'time', fallback: '—', invalidFallback: '—' })}
        </div>
      </div>
    </div>
  );
}
